import {
  useQuery,
  queryOptions,
  UseInfiniteQueryOptions,
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
} from "@tanstack/react-query"

import { api } from "@/lib/api-client"
import { QueryConfig } from "@/lib/react-query"

import { BuildingResponse } from "../types/api"

type BuildingManagedQueryParams = {
  search?: string
  pageIndex?: number
  pageSize?: number
  company_id?: string
  id?: string
}

export const getBuildingsManaged = ({
  search,
  pageIndex,
  pageSize,
  company_id,
  id,
}: BuildingManagedQueryParams): Promise<BuildingResponse> => {
  return api.get("/buildings/managed", {
    params: {
      search,
      page: pageIndex,
      limit: pageSize,
      company_id,
      id,
    },
    headers: {
      "Active-Group-ID": "1",
    },
  })
}

export const getBuildingsManagedQueryOptions = ({
  search,
  pageIndex,
  pageSize,
  company_id,
  id,
}: BuildingManagedQueryParams) => {
  return queryOptions({
    queryKey: [
      "buildings-managed",
      { search, pageIndex, pageSize, company_id, id },
    ],
    queryFn: () =>
      getBuildingsManaged({ search, pageIndex, pageSize, company_id, id }),
  })
}

type UseBuildingsManagedOptions = {
  queryConfig?: QueryConfig<typeof getBuildingsManagedQueryOptions>
} & BuildingManagedQueryParams

export const useBuildingsManaged = ({
  search,
  pageIndex,
  pageSize,
  company_id,
  id,
  queryConfig,
}: UseBuildingsManagedOptions = {}) => {
  return useQuery({
    ...getBuildingsManagedQueryOptions({
      search,
      pageIndex,
      pageSize,
      company_id,
      id,
    }),
    ...queryConfig,
  })
}

export const getBuildingsManagedInfiniteScroll = ({
  search,
  pageIndex,
  pageSize,
  company_id,
  id,
}: BuildingManagedQueryParams): Promise<BuildingResponse> => {
  return api.get("/buildings/managed", {
    params: {
      search,
      page: pageIndex,
      limit: pageSize,
      company_id,
      id,
    },
    headers: {
      "Active-Group-ID": "1",
    },
  })
}

export const getBuildingsManagedInfiniteScrollQueryOptions = ({
  search,
  pageIndex,
  pageSize,
  company_id,
  id,
}: BuildingManagedQueryParams) => {
  // Define the specific QueryKey type
  type BuildingsManagedQueryKey = [
    "buildings-managed-infinite-scroll",
    {
      search?: string
      pageIndex?: number
      pageSize?: number
      company_id?: string
      id?: string
    },
  ]

  return queryOptions({
    queryKey: [
      "buildings-managed-infinite-scroll",
      { search, pageIndex, pageSize, company_id, id },
    ] as BuildingsManagedQueryKey, // Assert queryKey type
    queryFn: ({ pageParam }: any) =>
      getBuildingsManagedInfiniteScroll({
        search,
        pageIndex: pageParam,
        pageSize,
        company_id,
        id,
      }),
  })
}

type UseBuildingsManagedInfiniteScrollOptions = {
  queryConfig?: Omit<
    UseInfiniteQueryOptions<
      BuildingResponse,
      Error,
      InfiniteData<BuildingResponse>,
      QueryKey // This QueryKey needs to match the one in queryOptions
    >,
    "queryKey" | "queryFn"
  >
} & BuildingManagedQueryParams

export const useBuildingsManagedInfiniteScroll = ({
  search,
  pageIndex,
  pageSize,
  company_id,
  id,
  queryConfig,
}: UseBuildingsManagedInfiniteScrollOptions = {}) => {
  return useInfiniteQuery<
    BuildingResponse,
    Error,
    InfiniteData<BuildingResponse>,
    QueryKey, // Ensure this matches what's used in queryOptions
    number | undefined
  >({
    ...getBuildingsManagedInfiniteScrollQueryOptions({
      search,
      pageIndex,
      pageSize,
      company_id,
      id,
    }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.meta.page
      const totalPage = lastPage.meta.total_page
      const nextPage = currentPage + 1

      return nextPage <= totalPage ? nextPage : undefined
    },
    initialPageParam: pageIndex,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...(queryConfig as any),
  })
}
